const _ = require('lodash');
const { CHANNEL_DETAILS, CHANNELS_WITH_KEYWORDS, CHANNEL_STATUS_TO_STATUS } = require('./constants.js');
const resultIndicatorUtils = require('./utils/resultIndicatorUtils');
const { capitalize } = require('./utils');
const customMetricHelpers = require('./customMetric');
const metricUtils = require('./utils/metricUtils');
const DETAIL_NAMES = [
  'age',
  'gender',
  'location',
  'searchKeywords',
  'deviceTarget',
  'placement',
  'subChannel',
  'creativeTitle',
  'creativeDescription',
  'creativeImage',
  'platformPlacement',
  'hourOfDay',
  'country',
  'os',
];
const moment = require('moment');
const { isServer } = require('./utils/serverUtils.js');

/**
 *
 * @typedef {import("../types.js").DateFilter} DateFilter
 * @typedef {import("../types.js").ArchitectureLevel} ArchitectureLevel
 * @typedef {import("../types.js").DateGroup} DateGroup
 * @typedef {import("../types.js").ConnectAdAccount} ConnectAdAccount
 * @typedef {import("../types.js").ConnectElement} ConnectElement
 * @typedef {import("../types.js").ConnectReport} ConnectReport
 * @typedef {import("../types.js").ArchitectureFilter} ArchitectureFilter
 * @typedef {import("../types.js").ConnectPartialArchi} ConnectPartialArchi
 * @typedef {import("../types.js").DashboardTable} DashboardTable
 * @typedef {import("../types.js").DashboardTableCell} DashboardTableCell
 * @typedef {import("../types.js").DashboardTableRow} DashboardTableRow
 * @typedef {import("../types.js").DashboardFilter} DashboardFilter
 * @typedef {import("../types.js").MetricFilter} MetricFilter
 * @typedef {import("../types.js").DimensionId} DimensionId
 */

/**
 *
 * @param {object} p
 * @param {DashboardTableRow[]} p.rows
 * @param {string} p.dimensionId
 * @param {string} [p.childrenDimensionId]
 * @param {function} [p.additionalMerger]
 * @param {boolean} [p.isCustomTable]
 * @param {DashboardFilter} [p.dashboardFilter]
 * @param {CustomMetric[]} [p.customMetrics]
 * @returns {DashboardTableRow[]}
 */
function mergeDashboardTableRowsByDimension({
  rows,
  dimensionId,
  childrenDimensionId = null,
  additionalMerger = null,
  isCustomTable = false,
  dashboardFilter,
  currency,
  customMetrics,
}) {
  if (!rows?.length) return [];
  let groupedRows;
  if (['month', 'week', 'day', 'autoTime'].includes(dimensionId)) {
    groupedRows = _.groupBy(rows, row => `${row.from}->${row.to}`);
  } else if (['all'].includes(dimensionId)) {
    groupedRows = { all: rows };
  } else if (['adAccount', 'campaign', 'adSet', 'ad', 'keyword'].includes(dimensionId)) {
    let idName = 'channel' + capitalize(dimensionId) + 'Id';
    groupedRows = _.groupBy(rows, row => row.channel + ':' + dimensionId + ':' + row[idName]);
  } else if (typeof dimensionId == 'number' || parseInt(dimensionId)) {
    groupedRows = _.groupBy(rows, row => dimensionId + ':' + row?.dimensions?.[dimensionId]?.id);
  } else if (dimensionId?.type == 'customBreakDown') {
    groupedRows = _.groupBy(rows, row => dimensionId.id + ':' + row?.dimensions?.[dimensionId.id]?.id);
  } else if (dimensionId == 'subChannel') {
    groupedRows = _.groupBy(rows, row => (row.detailValue ? row.detailValue : row.subChannel));
  } else if (DETAIL_NAMES.includes(dimensionId)) {
    groupedRows = _.groupBy(rows, row => row.detailValue || 'undetermined');
  } else if (dimensionId == 'channel') {
    groupedRows = _.groupBy(rows, row => row.subChannel || row.channel);
  } else if (dimensionId == 'utm_chanel') {
    groupedRows = _.groupBy(rows, 'utm');
  } else if ('campaignSubChannel'.includes(dimensionId) && dimensionId != '') {
    groupedRows = _.groupBy(rows, 'rowId');
  } else if (['ga', 'af', 'gss', 'ms', 'pg', 'rs', 'mailchimp', 'adverity'].includes(dimensionId.split(':')[0])) {
    groupedRows = _.groupBy(rows, 'rowId');
  } else if (dimensionId == 'weekDate') {
    groupedRows = _.groupBy(rows, 'name');
  } else if (['creativeWord', 'imageHash', 'status'].includes(dimensionId)) {
    groupedRows = _.groupBy(rows, row => row.id);
  } else if (['colorClustering', 'status'].includes(dimensionId)) {
    groupedRows = _.groupBy(rows, row => row.id);
  } else if (['emojiAnalysis', 'status'].includes(dimensionId)) {
    groupedRows = _.groupBy(rows, row => row.id);
  } else if (dimensionId == 'os') {
    groupedRows = _.groupBy(rows, 'os');
  } else if (dimensionId == 'objectives') {
    groupedRows = _.groupBy(rows, row =>
      row.cell.objectives.length === 1 ? row.cell.objectives[0] : 'MULTI_OBJECTIVES',
    );
  } else if (dimensionId === 'status') {
    groupedRows = _.groupBy(rows, row => row.id);
  } else if (isCustomTable) {
    groupedRows = _.groupBy(rows, row => row?.id ?? row?.dimension?.id);
  } else {
    throw new Error(`Unknown dimensionId: '${dimensionId}'`);
  }
  let mergedRows = Object.entries(groupedRows).map(([key, rows]) => {
    if (!rows?.length) return { children: [], cell: {} };
    let mergedRow = mergeDashboardTableRows(rows, customMetrics);
    mergedRow.rowId = mergedRow.tableId = key;
    if (typeof dimensionId == 'number') {
      mergedRow.level = 'customDimension';
      mergedRow.dimension = { id: dimensionId, name: mergedRow.dimensionName };
    } else {
      mergedRow.level = dimensionId;
      if (['month', 'week', 'day', 'autoTime', 'weekDate'].includes(dimensionId)) mergedRow.level = 'dateGroup';
      mergedRow.dimension = { id: dimensionId, name: capitalize(dimensionId?.type || dimensionId) };
    }
    if (childrenDimensionId) {
      mergedRow.children = mergeDashboardTableRowsByDimension({
        rows: rows.flatMap(row => row.children),
        dimensionId: childrenDimensionId,
        customMetrics,
      });
    }

    if (dimensionId === 'creativeWord' && mergedRow?.cell?.wordOccurrences == null) {
      mergedRow.cell.wordOccurrences = rows.length;
    }
    if (dimensionId === 'colorClustering' && mergedRow?.cell?.clusterSize == null) {
      mergedRow.cell.clusterSize = rows.length;
    }
    if (dimensionId === 'emojiAnalysis' && mergedRow?.cell?.emojiSize == null) {
      mergedRow.cell.emojiSize = rows.length;
    }
    // mergedRow.name = mergedRow.dimension.name
    if (additionalMerger) mergedRow = additionalMerger({ mergedRow, rows });
    return mergedRow;
  });
  if (dimensionId === 'creativeWord' && mergedRows?.[0]?.cell?.wordTotalCount == null) {
    const wordTotalCount = mergedRows.reduce((acc, current) => acc + current.cell.wordOccurrences, 0);
    mergedRows.forEach(x => {
      x.cell.wordTotalCount = wordTotalCount;
    });
  }
  mergedRows = getFilteredRows(mergedRows, dashboardFilter, currency);
  mergedRows = _.sortBy(mergedRows, 'rowId');
  // @ts-ignore
  return mergedRows;
}

/**
 * Merge several rows of a dashboard table into one with the sum of all metrics, actions, etc...
 * Merged 'reach' cannot be accurately computed.
 * Output and input use FE format.
 * @param {DashboardTableRow[]} rows
 * @returns {DashboardTableRow}
 */
function mergeDashboardTableRows(rows, customMetrics = []) {
  if (rows.length == 0) return null;
  const resultIndicator = resultIndicatorUtils.getResultIndicator(rows);
  let mergedRow = { ...rows[0], cell: _.cloneDeep(rows[0]?.cell || {}) };
  let mergedCell = mergedRow.cell;
  for (let row of rows.slice(1)) {
    // Loop on all metrics
    let cell = row.cell;
    for (let [metricName, metricValue] of Object.entries(cell)) {
      // Add up metric together.
      // Computed metrics (ex: cpc, cpa, ctr) will not be accurate.
      // But, they will be recomputed later.
      if (typeof metricValue == 'number') {
        mergedCell[metricName] = (mergedCell[metricName] || 0) + metricValue;
      } else if (metricName == 'subChannels') {
        mergedCell[metricName] = _.union(mergedCell[metricName], metricValue);
      } else if (metricName === 'resultIndicator' && mergedCell[metricName] !== metricValue) {
        mergedCell[metricName] = 'MULTIPLE_GOAL';
      } else if (['budget'].includes(metricName)) {
        if (metricValue) {
          if (!mergedCell[metricName])
            mergedCell[metricName] = {
              lifeTime: 0,
              daily: 0,
            };
          if (row.level == 'adAccount') {
            if (mergedCell[metricName].lifeTime < metricValue.lifeTime) {
              mergedCell[metricName].lifeTime = metricValue.lifeTime;
            }
            if (mergedCell[metricName].daily < metricValue.daily) {
              mergedCell[metricName].daily = metricValue.daily;
            }
          } else {
            if (metricValue.canBeSummed) {
              mergedCell[metricName].lifeTime += metricValue.lifeTime;
              mergedCell[metricName].daily += metricValue.daily;
            } else {
              mergedCell[metricName].lifeTime = metricValue.lifeTime;
              mergedCell[metricName].daily = metricValue.daily;
            }
          }
        } else {
          // keep mergedCell[metricName] unchanged
        }
      } else if (['utms', 'isUtmInstalled'].includes(metricName)) {
        if (metricName == 'utms') {
          mergedCell[metricName] = _.uniqBy([...(mergedCell[metricName] || []), ...(metricValue || [])], v =>
            JSON.stringify(v),
          );
        }
        if (metricName == 'isUtmInstalled') {
          mergedCell[metricName] = mergedCell[metricName] || metricValue;
        }
      } else if (
        metricName == 'objectives' ||
        (['schedule'].includes(metricName) && ['ad', 'adSet', 'campaign'].includes(row.level))
      ) {
        if (JSON.stringify(mergedCell[metricName]) != JSON.stringify(metricValue)) {
          if (!mergedCell?.[metricName]?.length && !metricValue?.length) {
            mergedCell[metricName] = [];
          } else if (!mergedCell?.[metricName]?.length || !metricValue?.length) {
            mergedCell[metricName] = mergedCell?.[metricName]?.length ? mergedCell[metricName] : metricValue;
          } else {
            mergedCell[metricName] = ['MULTI_OBJECTIVES'];
          }
        }
      } else {
        // Erase non-number metrics, except if they have the same values in all the rows.
        if (mergedCell[metricName] != metricValue) mergedCell[metricName] = undefined;
      }
    }
    // Loop on the settings
    for (let [attr, value] of Object.entries(row)) {
      if (['cell', 'kpi'].includes(attr)) continue;
      // Don't merge budgets or schedules.
      else if (['budget', 'schedule'].includes(attr) && !['ad', 'adSet', 'campaign'].includes(row.level))
        mergedRow[attr] = undefined;
      // Merge only when channelId is equal
      else if (['budget', 'schedule'].includes(attr) && ['ad', 'adSet', 'campaign'].includes(row.level)) {
        if (mergedRow.channelId !== undefined && mergedRow.channelId == row.channelId) {
          mergedRow[attr] = mergedRow[attr] || row[attr];
        }
      }
      // Merge dimensions
      else if (attr == 'dimensions') {
        for (let dimensionId of Object.keys(mergedRow.dimensions)) {
          if (!row.dimensions[dimensionId] || row.dimensions[dimensionId].id != mergedRow.dimensions[dimensionId].id)
            delete mergedRow.dimensions[dimensionId];
        }
      } else if (attr == 'childCursors') {
        if (Object.isFrozen(mergedRow.childCursors)) {
          mergedRow.childCursors = { ...mergedRow.childCursors, value };
        } else {
          Object.assign(mergedRow.childCursors, value);
        }
      } else if (attr == 'creative') {
        mergedRow.creative = mergedRow.creative || row.creative;
      } else if (attr == 'isUtmInstalled' || attr == 'utms') {
        mergedRow[attr] = mergedRow[attr] || row[attr];
      }
      // Do not merge id the settings ae not the same in all rows.
      else if (mergedRow[attr] != value) mergedRow[attr] = undefined;
    }
  }
  // Recompute computed metrics (ex: cpc, cpa, ctr)
  mergedRow.cell = addComputedMetrics(mergedCell);
  customMetricHelpers.addCustomMetrics({ cell: mergedRow.cell, customMetrics, resultIndicator });

  // Move some metrics from cell to row
  mergedRow = moveMetricsToRow(mergedRow);

  return mergedRow;
}

/**
 *  extract information from the cell name
 * @param {string} conversionAttr
 * @returns {Object} info
 */
function getConversionAttrInfo(conversionAttr) {
  const [, tracker, ...conversionNameSplitted] = conversionAttr.split(':');
  const conversionName = `${tracker}:${conversionNameSplitted.join(':')}`;
  const conversionNameWithoutAttributionWindow = `${tracker}:${conversionNameSplitted[0]}`;
  return { tracker, conversionName, conversionNameWithoutAttributionWindow };
}

function getViewNames({ prefix, c }) {
  return _.uniq(
    Object.keys(c)
      .filter(k => k.startsWith(prefix))
      .map(k => k.replace(prefix, '')),
  );
}

/**
 * Compute metrics that are computed from other metrics. For ex, cpc is computed from clicks and spend
 * @param {DashboardTableCell} cell
 * @returns {DashboardTableCell}
 */
function addComputedMetrics(cell) {
  let c = _.cloneDeep(cell);
  c.cpc = c.spend / c.clicks;
  c.ctr = c.clicks / c.impressions;
  c.costPerOutboundClick = c.spend / c.outboundClicks;
  c.outboundClicksCtr = c.outboundClicks / c.impressions;
  c.cpm = (c.spend / c.impressions) * 1000;
  c.cpa = c.spendOnActions / c.conversions;
  c.cpaWithTotalSpend = c.spend / c.conversions;
  c.conversionRate = c.conversions / c.clicksOnActions;
  c.frequency = c.impressions / c.reach;
  c.pixelRoas = c.pixelRevenue / c.spend;
  c.gaRoas = c.gaRevenue / c.spend;
  c.videoViewsRate = c.videoViews / c.videoImpressions;
  c.cpv = c.spendOnViews / c.videoViews;
  c.amountSpentPercentage = c.lifeTimeSpend / c.lifeTimeBudget;
  c.videoP25Rate = c.videoP25 / c.videoImpressions;
  c.videoP50Rate = c.videoP50 / c.videoImpressions;
  c.videoP75Rate = c.videoP75 / c.videoImpressions;
  c.videoP100Rate = c.videoP100 / c.videoImpressions;
  c.wordFrequency = c.wordOccurrences / c.wordTotalCount;
  c.costPerMessagingFirstReply = c.spend / c.messagingFirstReply;
  c.costPerMessagingStarted7d = c.spend / c.messagingStarted7d;
  if (c.clicksAll == null) {
    c.clicksAll = c.clicks;
  }
  // if (!['campaign', 'adSet', 'ad'].includes(c.level))
  //   c.status = undefined
  for (let conversionAttr of Object.keys(cell).filter(k => k.startsWith('conversions:'))) {
    const { tracker, conversionName, conversionNameWithoutAttributionWindow } = getConversionAttrInfo(conversionAttr);
    let spendOnActions = c['spendOnActions:' + conversionNameWithoutAttributionWindow];
    let clicksOnActions = c['clicksOnActions:' + conversionNameWithoutAttributionWindow];
    if (['googleAnalytics', 'appsFlyer'].includes(tracker)) {
      spendOnActions = c.spend;
      clicksOnActions = c.clicks;
    }
    const conversions = c['conversions:' + conversionName];
    const conversionValue = c[`conversionValue:${conversionName}`];
    c['cpa:' + conversionName] = spendOnActions / conversions;
    c['cpaWithTotalSpend:' + conversionName] = c.spend / conversions;
    c['conversionRate:' + conversionName] = conversions / clicksOnActions;
    c[`roas:${conversionName}`] = conversionValue / spendOnActions;
  }
  // GA ROAS
  getViewNames({ prefix: 'googleAnalytics:transactionRevenue', c }).map(viewName => {
    c['roas:googleAnalytics:purchase' + viewName] = c['googleAnalytics:transactionRevenue' + viewName] / c.spend;
  });
  // recalculate ga metrics
  getViewNames({ prefix: 'googleAnalytics:bounces', c }).map(viewName => {
    if (c['googleAnalytics:sessions' + viewName]) {
      c['googleAnalytics:bounceRate' + viewName] =
        c['googleAnalytics:bounces' + viewName] / c['googleAnalytics:sessions' + viewName];
    }
  });
  getViewNames({ prefix: 'googleAnalytics:sessionDuration', c }).map(viewName => {
    if (c['googleAnalytics:sessions' + viewName]) {
      c['googleAnalytics:averageSessionDuration' + viewName] =
        c['googleAnalytics:sessionDuration' + viewName] / c['googleAnalytics:sessions' + viewName];
    }
    //delete c["googleAnalytics:sessionDuration" + viewName];
  });
  if (c['appsFlyer:transactionRevenue:total']) {
    c['roas:appsFlyer:total'] = c['appsFlyer:transactionRevenue:total'] / c.spend;
  }
  if (c.resultIndicator === 'MULTIPLE_GOAL') {
    c.resultIndicator = 'clicks';
  }
  c.results = c[c.resultIndicator];
  c.cpr = c.spend / c.results;
  c.cvr = c.results / c.impressions;
  // recalculate ga metrics
  if (c['googleAnalytics:sessionDuration'] && c['googleAnalytics:sessions']) {
    c['googleAnalytics:averageSessionDuration'] = c['googleAnalytics:sessionDuration'] / c['googleAnalytics:sessions'];
    //delete c["googleAnalytics:sessionDuration"];
  }
  // Remove undefined, nan, and infinite fields
  c = _.pickBy(
    c,
    (val, key) =>
      typeof val == 'string' ||
      (typeof val == 'object' && val != null) ||
      (!isNaN(val) && val != undefined && val != Infinity),
  );

  return c;
}

/**
 * Move metrics from cell to row
 * @param {DashboardTableRow} row
 * @returns {DashboardTableRow}
 */
function moveMetricsToRow(row) {
  const metricsToMove = ['utms', 'isUtmInstalled'];

  metricsToMove.map(metric => {
    if (typeof row.cell[metric] !== 'undefined') {
      row[metric] = row.cell[metric];
      delete row.cell[metric];
    }
  });

  return row;
}

/**
 *
 * @param {object} p
 * @param {ConnectAdAccount[]} p.adAccounts
 * @param {DashboardFilter} p.dashboardFilter
 * @param {DimensionId} p.dimensionId
 * @returns {{adAccounts:ConnectAdAccount[] ,dashboardFilter:DashboardFilter}}
 */
function filterAdAccounts({ adAccounts, dashboardFilter, dimensionId }) {
  let { adAccountIds, channels } = dashboardFilter;
  let isDetail = DETAIL_NAMES.includes(dimensionId) && dimensionId != 'subChannel';
  let isAllAdAccounts = adAccountIds?.includes('filter_all');
  if (isAllAdAccounts) {
    dashboardFilter.adAccountIds = [
      ...adAccountIds.filter(adAccountId => adAccountId != 'filter_all'),
      ...adAccounts
        // .filter(adAccount => !adAccount.is_analytic && !adAccount.isCustomReportAccount)
        .filter(adAccount => !adAccount.is_analytic)
        .map(adAccount => adAccount.channelId),
    ];
  }

  // if (!adAccountIds?.length) adAccountIds = null
  // if (!channels?.length) channels = null
  let filteredAdAccounts = adAccounts.filter(
    adAccount =>
      (!adAccountIds || isAllAdAccounts || adAccountIds.includes(String(adAccount.channelId))) &&
      (!channels ||
        channels.includes(adAccount.channel) ||
        adAccount.channel == 'externalFiles' ||
        channels.some(item => item.includes(adAccount.channel))) && // for google subChannels
      (!isDetail || CHANNEL_DETAILS[adAccount.channel]?.includes(dimensionId)) &&
      (dimensionId !== 'keyword' || CHANNELS_WITH_KEYWORDS.includes(adAccount.channel)),
  );
  //   let filteredDashboardFilter = _.omit(dashboardFilter, ["adAccountNames", "adAccountIds"])
  // if (!isDetail) {
  const analyticAndBlendedDataSources = adAccounts.filter(
    adAccount =>
      adAccount.is_analytic ||
      ((!adAccountIds || isAllAdAccounts || adAccountIds.includes(String(adAccount.channelId))) &&
        adAccount.isCustomReportAccount &&
        adAccount.settings &&
        Object.keys(adAccount.settings).length),
  );
  // @ts-ignore
  filteredAdAccounts.push(...analyticAndBlendedDataSources);
  // }
  filteredAdAccounts = _.uniq(filteredAdAccounts);
  return { adAccounts: filteredAdAccounts, dashboardFilter };
}

/**
 * @param {DashboardTableRow} row
 * @param {MetricFilter} metricFilter
 * @param {string} currency
 */
function isMetricFilterConditionMet(row, metricFilter, currency) {
  let rowValue = row.cell[metricFilter.field];
  if (metricUtils.getCellType(metricFilter.field) === 'percentage') {
    rowValue *= 100;
  }
  // skip if we the value is not there
  if (rowValue == null) {
    if (isServer() && row.isAnalytic) {
      return true;
    } else {
      return false;
    }
  }
  switch (metricFilter.operator) {
    case 'EQUAL':
      const DIFF = currency === 'KRW' ? 0.5 : 0.005;
      return Math.abs(rowValue - metricFilter.value) < DIFF;
    case 'GREATER_THAN':
      return rowValue > metricFilter.value;
    case 'LESS_THAN':
      return rowValue < metricFilter.value;
    default:
      return false;
  }
}

/**
 * @param {DashboardTableRow[]} rows
 * @param {DashboardFilter} dashboardFilter
 * @param {string} currency
 */
function getFilteredRows(rows, dashboardFilter, currency) {
  if (dashboardFilter?.metrics?.length > 0) {
    const metricFilters = dashboardFilter.metrics;
    rows = rows.filter(row =>
      metricFilters.every(metricFilter => isMetricFilterConditionMet(row, metricFilter, currency)),
    );
  }
  return rows;
}

/**
 * @param {object} p
 * @param {DashboardTableRow[]} p.table
 * @param {boolean} [p.prepareForSubtraction]
 * @param {string} p.dimensionId
 * @param {object} p.dimension
 */
function convertToOthersTable({ table, prepareForSubtraction = false, dimensionId, dimension }) {
  const others = 'others';
  const metricsToOthers = [
    'channel',
    'channelId',
    'channelAdAccountId',
    'id',
    'name',
    'rowId',
    'subChannel',
    'tableId',
    'detailValue',
    'os',
  ];
  const metricsToDelete = ['channelAdId', 'channelAdSetId', 'channelCampaignId', 'dimensions'];
  const childMetricsToOthers = ['id', 'channel', 'channelId', 'channelAdAccountId', 'channelCampaignId'];
  const childMetricsToDelete = ['videoAvgTime'];

  table.map(row => {
    // convert metrics to 'others'
    metricsToOthers.map(metric => {
      row[metric] = others;
    });

    // convert dimension and level to original dimension
    row.dimension = { id: dimension.id, name: dimension.name };
    row.level = dimensionId;

    // delete useless metrics
    metricsToDelete.map(metric => {
      delete row[metric];
    });

    // make value negative if it is for subtraction
    if (prepareForSubtraction) {
      Object.entries(row.cell).map(([metricName, metricValue]) => {
        if (typeof metricValue === 'number') {
          row.cell[metricName] = -1 * metricValue;
        }
      });
    }
    row.children.map(child => {
      // convert child metrics to 'others'
      childMetricsToOthers.map(metric => {
        child[metric] = others;
      });

      childMetricsToDelete.map(metric => {
        delete child[metric];
      });

      // make value negative if it is for subtraction
      if (prepareForSubtraction) {
        Object.entries(child.cell).map(([metricName, metricValue]) => {
          if (typeof metricValue === 'number') {
            // it was added after the change to query mode, some value was already negative
            if (metricValue > 0) {
              child.cell[metricName] = -1 * metricValue;
            }
          }
        });
      }
    });
  });
}

module.exports = {
  mergeDashboardTableRowsByDimension,
  mergeDashboardTableRows,
  filterAdAccounts,
  getFilteredRows,
  convertToOthersTable,
  DETAIL_NAMES,
};
